'use client';

import Link from 'next/link';

const autofixSteps = [
  { num: '01', title: 'Discover', desc: 'Continuous monitoring across CVEs, misconfigurations, exposed secrets, and compliance gaps. Full-stack asset intelligence across code repos, cloud, containers, and endpoints.', accent: 'emerald' },
  { num: '02', title: 'Prioritize', desc: 'Every finding scored by real exploitability, not just CVSS: EPSS, asset criticality, exposure, and blast radius. AutoFix traces each finding to its root cause before any fix is generated.', accent: 'emerald' },
  { num: '03', title: 'Generate', desc: 'AutoFix generates the fix - patching packages, rotating secrets, correcting IAM policies, hardening configs - and validates it in isolation before it goes anywhere near production. Every fix is proposed with an AI explainability report and waits for human sign-off.', accent: 'blue' },
  { num: '04', title: 'Apply', desc: 'Approved fixes are merged: pull requests, config changes, dependency updates. Your full regression suite and compliance checks must pass first. Human-gated.', accent: 'blue' },
  { num: '05', title: 'Deploy', desc: 'Verified fixes ship to production through your existing CI/CD pipeline with staged rollout and health checks at every step. Human-gated.', accent: 'emerald' },
  { num: '06', title: 'Rollback', desc: 'AutoFix verifies the fix in production. If anything unexpected surfaces post-deploy, rollback triggers in under 60 seconds. Zero downtime, zero data loss.', accent: 'emerald' },
];

const autoscanSteps = [
  { num: '01', title: 'Domain Verification', desc: "Add a DNS TXT record to prove ownership, the same mechanism Google Search Console uses. We never scan assets you haven't verified." },
  { num: '02', title: 'Asset Enumeration', desc: 'Subdomains, open ports, TLS configuration, DNS records, cloud exposure, and shadow IT mapped across your full external attack surface.' },
  { num: '03', title: 'Active Exploitation', desc: 'AI agents actively attempt exploitation: SQL injection payloads sent, default credentials tried, SSRF tested. Real attacker behaviour, not theoretical scanning.' },
  { num: '04', title: 'Evidence Collection', desc: "Every finding backed by real proof: screenshots, payloads, responses. Zero false positives. If it's in the report, it was exploited." },
  { num: '05', title: 'PDF Report in 20 min', desc: 'Executive summary and technical findings with remediation guidance. Same structure auditors expect from a $15K engagement. SOC 2 and ISO 27001 accepted.' },
];

const standards = [
  ['OWASP ASVS', 'Web application security verification'],
  ['OWASP MASVS', 'Mobile application security verification'],
  ['NIST SP 800-115', 'Technical guide to information security testing'],
  ['CIS Benchmarks', 'Cloud, OS, and infrastructure hardening'],
  ['SOC 2 / HIPAA', 'Compliance gap detection and evidence generation'],
  ['PCI-DSS', 'Payment card industry security standard coverage'],
];

export default function MethodologyClient() {
  return (
    <section className="relative pt-8 pb-24 max-w-5xl mx-auto">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(91, 149, 255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(91, 149, 255,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      {/* Hero */}
      <div className="mb-20">
        <p className="text-xs font-medium text-accent uppercase tracking-widest mb-4">Methodology</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight leading-tight mb-6">
          Detect. Fix. Verify.<br /><span className="text-accent">Never Break Prod.</span>
        </h1>
        <p className="text-lg text-zinc-400 max-w-2xl leading-relaxed">
          Cyber Army closes every loop: autonomous remediation that proves fixes before deployment, and AI penetration testing with real exploitation evidence.
        </p>
      </div>

      {/* AutoFix methodology */}
      <div className="mb-24">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-2">CyberArmy AutoFix</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white/90 tracking-tight">The six-step autonomous loop</h2>
          </div>
          <Link href="/autofix" className="text-sm text-accent hover:text-[#8fb6ff] transition-colors inline-flex items-center gap-1">
            Full details <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </Link>
        </div>
        <div className="rounded-2xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
          {autofixSteps.map((step) => (
            <div key={step.num}
              className="group flex gap-6 p-7 bg-surface-1 hover:bg-surface-2 transition-colors duration-200 border-l-2 border-transparent"
              onMouseEnter={e => (e.currentTarget.style.borderLeftColor = step.accent === 'emerald' ? '#5b95ff' : '#a78bfa')}
              onMouseLeave={e => (e.currentTarget.style.borderLeftColor = 'transparent')}
            >
              <div className="flex-shrink-0 flex flex-col items-center gap-2 w-12">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${step.accent === 'emerald' ? 'bg-accent/10 border border-accent/20 text-accent' : 'bg-accent-secondary/10 border border-accent-secondary/20 text-accent-secondary'}`}>
                  {step.num}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white/90 mb-1.5">{step.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AutoScan methodology */}
      <div className="mb-24">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-medium text-accent-secondary uppercase tracking-widest mb-2">AutoScan</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white/90 tracking-tight">AI penetration testing in five steps</h2>
          </div>
          <Link href="/autoscan" className="text-sm text-accent-secondary hover:text-accent-secondary-light transition-colors inline-flex items-center gap-1">
            Full details <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </Link>
        </div>
        <div className="rounded-2xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
          {autoscanSteps.map((step) => (
            <div key={step.num}
              className="group flex gap-6 p-7 bg-surface-1 hover:bg-surface-2 transition-colors duration-200 border-l-2 border-transparent"
              onMouseEnter={e => (e.currentTarget.style.borderLeftColor = '#a78bfa')}
              onMouseLeave={e => (e.currentTarget.style.borderLeftColor = 'transparent')}
            >
              <div className="flex-shrink-0 w-12">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold bg-accent-secondary/10 border border-accent-secondary/20 text-accent-secondary">{step.num}</div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white/90 mb-1.5">{step.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Standards */}
      <div className="mb-24">
        <p className="text-xs font-medium text-accent uppercase tracking-widest mb-4">Standards & alignment</p>
        <h2 className="text-2xl sm:text-3xl font-bold text-white/90 tracking-tight mb-8">OWASP, NIST, and CIS aligned.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {standards.map(([name, desc]) => (
            <div key={name} className="rounded-xl border border-white/[0.06] bg-surface-1 p-5">
              <p className="text-sm font-semibold text-white/80 mb-1">{name}</p>
              <p className="text-xs text-zinc-500">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl border border-accent/15 bg-gradient-to-br from-[#0a1e40]/20 via-[#0C0F18] to-[#0C0F18] p-10 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white/90 tracking-tight mb-4">See the methodology in action.</h2>
        <p className="text-zinc-400 text-sm mb-8 max-w-xl mx-auto">Request access to AutoScan or AutoFix and see real results on your environment.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link href="/beta-signup?product=AutoScan&source=methodology_cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-black font-semibold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 text-sm">
            Request Beta Access
          </Link>
          <Link href="/beta-signup?product=AutoFix&source=methodology_cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-all text-sm">
            Request AutoFix Early Access →
          </Link>
        </div>
      </div>
    </section>
  );
}
